import React, { useState } from "react";
import { Star } from "lucide-react";

const cx = (...a) => a.filter(Boolean).join(" ");

const SIZES = { sm: "h-3.5 w-3.5", md: "h-4 w-4", lg: "h-5 w-5" };

// Read-only star display supporting fractional values (e.g. 4.3 / 5).
export function StarRating({ value = 0, size = "md", showValue = false, count, className }) {
    const v = Math.max(0, Math.min(5, Number(value) || 0));
    const dim = SIZES[size] || SIZES.md;
    return (
        <span className={cx("inline-flex items-center gap-1", className)} data-testid="star-rating" aria-label={`${v} out of 5 stars`}>
            <span className="inline-flex items-center">
                {[0, 1, 2, 3, 4].map((i) => {
                    const fill = Math.max(0, Math.min(1, v - i)); // 0..1 for this star
                    return (
                        <span key={i} className="relative inline-block">
                            <Star className={cx(dim, "text-muted-foreground/30")} />
                            {fill > 0 && (
                                <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
                                    <Star className={cx(dim, "fill-brass text-brass")} />
                                </span>
                            )}
                        </span>
                    );
                })}
            </span>
            {showValue && <span className="text-sm font-semibold tabular-nums">{v.toFixed(1)}</span>}
            {count != null && <span className="text-xs text-muted-foreground">({count})</span>}
        </span>
    );
}

// Interactive star input (1..5).
export function StarInput({ value = 0, onChange, size = "lg", label, testId }) {
    const [hover, setHover] = useState(0);
    const dim = SIZES[size] || SIZES.lg;
    const active = hover || value;
    return (
        <div className="flex items-center justify-between gap-3" data-testid={testId}>
            {label && <span className="text-sm">{label}</span>}
            <span className="inline-flex items-center gap-0.5" onMouseLeave={() => setHover(0)}>
                {[1, 2, 3, 4, 5].map((i) => (
                    <button
                        key={i}
                        type="button"
                        onClick={() => onChange?.(i)}
                        onMouseEnter={() => setHover(i)}
                        className="rounded p-0.5 transition-transform duration-150 hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        aria-label={`${i} star${i > 1 ? "s" : ""}`}
                        data-testid={testId ? `${testId}-star-${i}` : undefined}
                    >
                        <Star className={cx(dim, i <= active ? "fill-brass text-brass" : "text-muted-foreground/40")} />
                    </button>
                ))}
            </span>
        </div>
    );
}
